
import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import FilterPopover from './components/filter/FilterPopover';
import { VideoFilterOptions } from '@/hooks/useVideoEvents';

interface CalendarHeaderProps {
  currentDate: Date;
  activeViewType: 'week' | 'month' | 'list';
  onPrevious: () => void;
  onNext: () => void;
  onToday: () => void;
  onViewChange: (view: 'week' | 'month' | 'list') => void;
  filterOptions: VideoFilterOptions;
  onFilterChange: (filters: VideoFilterOptions) => void;
  onClearFilters: () => void;
  activeFiltersCount: number;
}

const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  currentDate,
  activeViewType,
  onPrevious,
  onNext,
  onToday,
  onViewChange,
  filterOptions,
  onFilterChange,
  onClearFilters,
  activeFiltersCount
}) => {
  // State variables
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState<VideoFilterOptions>(filterOptions);

  // Update local filters when props change
  useEffect(() => {
    setLocalFilters(filterOptions);
  }, [filterOptions]);

  // Callback functions
  const handleStatusChange = useCallback((status: string, checked: boolean) => {
    const currentStatuses = localFilters.statuses || [];
    const newStatuses = checked
      ? [...currentStatuses, status]
      : currentStatuses.filter(s => s !== status);
    
    setLocalFilters(prev => ({ ...prev, statuses: newStatuses }));
  }, [localFilters]);

  const handleContentTypeChange = useCallback((type: string, checked: boolean) => {
    const currentTypes = localFilters.contentTypes || [];
    const newTypes = checked
      ? [...currentTypes, type]
      : currentTypes.filter(t => t !== type);
    
    setLocalFilters(prev => ({ ...prev, contentTypes: newTypes }));
  }, [localFilters]);

  const handleNotesChange = useCallback((checked: boolean) => {
    setLocalFilters(prev => ({ ...prev, showNotes: checked }));
  }, []);

  const handleStartDateChange = useCallback((date: Date | undefined) => {
    setLocalFilters(prev => ({ ...prev, startDate: date }));
  }, []);

  const handleEndDateChange = useCallback((date: Date | undefined) => {
    setLocalFilters(prev => ({ ...prev, endDate: date }));
  }, []);

  const applyFilters = useCallback(() => {
    onFilterChange(localFilters);
    setIsFilterOpen(false);
  }, [localFilters, onFilterChange]);

  const clearFilters = useCallback(() => {
    onClearFilters();
    setIsFilterOpen(false);
  }, [onClearFilters]);

  const formatDate = useCallback((date: Date | undefined) => {
    return date ? format(date, 'dd MMM yyyy', { locale: fr }) : 'Non défini';
  }, []);

  const monthName = format(currentDate, "MMMM d", { locale: fr });
  
  return (
    <div className="flex items-center justify-between p-4 dark:bg-[#1a1d2d] dark:border-b dark:border-gray-800/50 font-sans">
      <div className="flex items-center space-x-4">
        <Button variant="ghost" onClick={onToday} className="font-sans dark:text-gray-300 dark:hover:bg-gray-800/60">
          Aujourd'hui
        </Button>
        <div className="flex items-center">
          <Button variant="ghost" size="icon" onClick={onPrevious} className="dark:text-gray-300 dark:hover:bg-gray-800/60">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={onNext} className="dark:text-gray-300 dark:hover:bg-gray-800/60">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="text-lg font-medium font-heading text-custom-dark dark:text-gray-200">
          {activeViewType === 'month' 
            ? format(currentDate, 'MMMM yyyy', { locale: fr })
            : monthName}
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <div className="flex border rounded-md dark:border-gray-700">
          <Button 
            variant={activeViewType === 'list' ? "secondary" : "ghost"} 
            size="sm"
            onClick={() => onViewChange('list')}
            className={`rounded-none rounded-l-md font-sans ${
              activeViewType === 'list' 
                ? 'dark:bg-custom-secondaryLight dark:text-custom-dark' 
                : 'dark:text-gray-300 dark:bg-transparent dark:hover:bg-gray-800/60 dark:border-gray-700'
            }`}
          >
            Liste
          </Button>
          <Button 
            variant={activeViewType === 'week' ? "secondary" : "ghost"} 
            size="sm"
            onClick={() => onViewChange('week')}
            className={`rounded-none font-sans ${
              activeViewType === 'week' 
                ? 'dark:bg-custom-secondaryLight dark:text-custom-dark' 
                : 'dark:text-gray-300 dark:bg-transparent dark:hover:bg-gray-800/60 dark:border-gray-700'
            }`}
          >
            Semaine
          </Button>
          <Button 
            variant={activeViewType === 'month' ? "secondary" : "ghost"} 
            size="sm"
            onClick={() => onViewChange('month')}
            className={`rounded-none rounded-r-md font-sans ${
              activeViewType === 'month' 
                ? 'dark:bg-custom-secondaryLight dark:text-custom-dark' 
                : 'dark:text-gray-300 dark:bg-transparent dark:hover:bg-gray-800/60 dark:border-gray-700'
            }`}
          >
            Mois
          </Button>
        </div>
        <FilterPopover
          isOpen={isFilterOpen}
          setIsOpen={setIsFilterOpen}
          localFilters={localFilters}
          activeFiltersCount={activeFiltersCount}
          handleStatusChange={handleStatusChange}
          handleContentTypeChange={handleContentTypeChange}
          handleNotesChange={handleNotesChange}
          handleStartDateChange={handleStartDateChange}
          handleEndDateChange={handleEndDateChange}
          applyFilters={applyFilters}
          clearFilters={clearFilters}
          formatDate={formatDate}
        />
      </div>
    </div>
  );
};

export default CalendarHeader;
